<template>
  <AccordionItem :is-open="true">
    <template #title>{{ filterLabel }}</template>
    <PrimeVueFloatLabel
      variant="on"
      v-if="FILTER_TYPES_WITH_SEARCH.includes(filterName)"
    >
      <PrimeVueIconField>
        <PrimeVueInputText
          :id="searchFieldId"
          v-model="facetQuery"
          type="search"
          rounded
          name="search"
          size="small"
          autocomplete="off"
          class="w-100"
        >
        </PrimeVueInputText>
        <PrimeVueInputIcon>
          <icon :name="'action_bar_magnifier'" size="small" color="black" />
        </PrimeVueInputIcon>
      </PrimeVueIconField>
      <label for="search-value">{{
        $textDictionary['downloadCenter.filter.searchLabel'] ?? 'Search'
      }}</label>
    </PrimeVueFloatLabel>
    <ExpandableInputList
      :key="filterName"
      type="checkbox"
      :label="filterLabel"
      :name="filterName"
      :value="activeValues"
      :options="options"
      :show-more-label="
        $textDictionary['productsList.filters.button.showmore.label']
      "
      :show-less-label="
        $textDictionary['productsList.filters.button.showless.label']
      "
      :displayed-options="5"
      @change="(name: string, value: string | string[]) => store.handleFormValueChange(name, value)"
    />
  </AccordionItem>
</template>

<script setup lang="ts">
import {
  useDownloadStore,
  FILTER_TYPES_WITH_SEARCH
} from '~/stores/downloadStore/downloadStore';
import { AccordionItem, Icon } from '@hypercodestudio/basler-components';
import ExpandableInputList from '../../shop/ExpandableInputList.vue';
import { debounce } from '~/composables/useDebounce';
import { useFilterValueLabel } from '~/stores/downloadStore/useFilterValueLabel';
import type { FacetValueModel } from '~/lib/DownloadService/model';

interface Props {
  filterItems: FacetValueModel[];
  filterName: string;
}

const props = defineProps<Props>();

const { $textDictionary } = useNuxtApp();
const store = useDownloadStore();
const logger = useLogger();

const facetQuery = ref('');
const searchFieldId = useId();
const facetSearchResult = ref<Record<string, number> | null>(null);

// XXX: hardcoded?
const filterLabelPrefix = 'productDocumentDownloadTab.';

const activeValues = computed<string[] | undefined>(
  () => store.filters[props.filterName]
);
const filterLabel = computed(
  () =>
    $textDictionary.value[filterLabelPrefix + props.filterName + '.label'] ??
    props.filterName
);

const options = computed<Record<string, string>>(() => {
  const optionsWithLabels: Record<string, string> = {};

  // we always show active filters as a facet search might hide those
  // (or they are not included in the default filters).
  /*activeValues.value?.forEach((value) => {
    if (!optionsWithLabels[value]) {
      optionsWithLabels[value] = useFilterValueLabel(props.filterName, value);
    }
  });*/

  if (facetSearchResult.value) {
    Object.entries(facetSearchResult.value).forEach(([key, count]) => {
      optionsWithLabels[key] = `${useFilterValueLabel(
        props.filterName,
        key
      )} (${count})`;
    });
  } else {
    props.filterItems.forEach(({ value, count }) => {
      optionsWithLabels[value] = `${useFilterValueLabel(
        props.filterName,
        value
      )} (${count})`;
    });
  }

  return optionsWithLabels;
});

watch(
  facetQuery,
  debounce(async () => {
    if (!facetQuery.value.trim()) {
      facetSearchResult.value = null;

      return;
    }

    try {
      facetSearchResult.value =
        (await store.facetSearch(props.filterName, facetQuery.value)) ?? null;
    } catch (e) {
      logger.error('error during facet search', e);
    }
  }, 300)
);
</script>
